import React, { useEffect, useState } from "react";
import { X, Loader2, Save, Trash2, Plus, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { api, PROJECT_CATEGORIES } from "../../lib/site";
import ImageUpload from "../ImageUpload";

const empty = {
  title: "", category: "Terrasse", location: "Halden", description: "",
  cover_image_id: null, cover_image_url: "",
  image_ids: [], external_images: [],
  before_after: [], // [{before_url, after_url, before_id, after_id, caption}]
  completion_date: "", published: true,
};

export default function ProjectEditor({ open, project, onClose, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [cover, setCover] = useState(null);

  useEffect(() => {
    if (project) {
      setForm({ ...empty, ...project, before_after: project.before_after || [] });
      setCover(project.cover_image_id ? { id: project.cover_image_id } : (project.cover_image_url ? { url: project.cover_image_url } : null));
      const g = [...(project.image_ids || []).map(id => ({ id })), ...(project.external_images || []).map(u => ({ url: u }))];
      setGallery(g);
    } else { setForm(empty); setCover(null); setGallery([]); }
  }, [project, open]);

  if (!open) return null;

  const setPair = (i, patch) => setForm(f => ({ ...f, before_after: f.before_after.map((p, j) => j === i ? { ...p, ...patch } : p) }));
  const addPair = () => setForm(f => ({ ...f, before_after: [...f.before_after, { before_url: "", after_url: "", caption: "" }] }));
  const delPair = (i) => setForm(f => ({ ...f, before_after: f.before_after.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title, category: form.category, location: form.location, description: form.description,
        cover_image_id: cover?.id || null, cover_image_url: cover?.id ? null : (cover?.url || null),
        image_ids: gallery.filter(g => g.id).map(g => g.id),
        external_images: gallery.filter(g => !g.id && g.url).map(g => g.url),
        before_after: form.before_after.filter(p => (p.before_url || p.before_id) && (p.after_url || p.after_id)),
        completion_date: form.completion_date, published: form.published,
      };
      if (project?.id) await api.patch(`/admin/projects/${project.id}`, payload);
      else await api.post(`/admin/projects`, payload);
      toast.success(project?.id ? "Prosjekt oppdatert" : "Prosjekt lagt til");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke lagre"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!project?.id || !window.confirm("Slette prosjektet?")) return;
    try { await api.delete(`/admin/projects/${project.id}`); toast.success("Slettet"); onSaved(); onClose(); }
    catch (e) { toast.error("Kunne ikke slette"); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sm:hidden pt-2 pb-1 grid place-items-center"><div className="h-1.5 w-10 rounded-full bg-[#D5D0C7]" /></div>
        <div className="p-4 sm:p-5 border-b border-[#E2DDD5] flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display text-lg sm:text-xl font-bold">{project?.id ? "Rediger prosjekt" : "Nytt prosjekt"}</h3>
          <button onClick={onClose} className="p-2 -m-2" data-testid="proj-close"><X size={20}/></button>
        </div>
        <div className="p-4 sm:p-5 space-y-4">
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Tittel</span>
            <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="proj-title" />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Kategori</span>
              <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="proj-category">
                {PROJECT_CATEGORIES.filter(c => c !== "Alle").map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Sted</span>
              <input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="proj-location" />
            </label>
          </div>
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Beskrivelse</span>
            <textarea rows={4} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="proj-description" />
          </label>
          <ImageUpload label="Hovedbilde (cover)" mode="single" scope="project" value={cover} onChange={setCover} />
          <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">... eller cover-URL</span>
            <input value={cover?.url && !cover?.id ? cover.url : ""} onChange={(e) => setCover(e.target.value ? { url: e.target.value } : null)} placeholder="https://..." className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="proj-cover-url" />
          </label>
          <ImageUpload label="Galleri" mode="multi" scope="project" value={gallery} onChange={setGallery} />

          {/* BEFORE / AFTER PAIRS */}
          <div className="rounded-xl border border-[#E2DDD5] bg-[#FAF8F5] p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-wider text-[#555E68] flex items-center gap-1.5"><ArrowLeftRight size={12}/> Før/etter-bilder</div>
              <button type="button" onClick={addPair} className="text-sm font-semibold text-[#1B3626] hover:underline inline-flex items-center gap-1" data-testid="proj-ba-add"><Plus size={14}/> Legg til par</button>
            </div>
            {form.before_after.length === 0 && <div className="text-xs text-[#7f7a68]">Ingen før/etter-par lagt til enda.</div>}
            <div className="space-y-4">
              {form.before_after.map((p, i) => (
                <div key={i} className="rounded-lg bg-white border border-[#E2DDD5] p-3">
                  <div className="flex items-center justify-between mb-2"><div className="text-xs font-bold text-[#1B3626]">Par #{i + 1}</div><button onClick={() => delPair(i)} className="text-[#8C2F19] p-1" data-testid={`proj-ba-del-${i}`}><Trash2 size={14}/></button></div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <ImageUpload label="Før-bilde" mode="single" scope="project" value={p.before_id ? { id: p.before_id } : (p.before_url ? { url: p.before_url } : null)} onChange={(v) => setPair(i, { before_id: v?.id || null, before_url: v?.id ? null : (v?.url || "") })} />
                      <input value={p.before_url && !p.before_id ? p.before_url : ""} onChange={(e) => setPair(i, { before_url: e.target.value, before_id: null })} placeholder="eller URL" className="mt-2 w-full rounded-md border border-[#D5D0C7] px-3 py-2 text-xs" data-testid={`proj-ba-before-url-${i}`} />
                    </div>
                    <div>
                      <ImageUpload label="Etter-bilde" mode="single" scope="project" value={p.after_id ? { id: p.after_id } : (p.after_url ? { url: p.after_url } : null)} onChange={(v) => setPair(i, { after_id: v?.id || null, after_url: v?.id ? null : (v?.url || "") })} />
                      <input value={p.after_url && !p.after_id ? p.after_url : ""} onChange={(e) => setPair(i, { after_url: e.target.value, after_id: null })} placeholder="eller URL" className="mt-2 w-full rounded-md border border-[#D5D0C7] px-3 py-2 text-xs" data-testid={`proj-ba-after-url-${i}`} />
                    </div>
                  </div>
                  <input value={p.caption || ""} onChange={(e) => setPair(i, { caption: e.target.value })} placeholder="Bildetekst (valgfritt)" className="mt-2 w-full rounded-md border border-[#D5D0C7] px-3 py-2 text-xs" data-testid={`proj-ba-caption-${i}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 items-end">
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#555E68]">Ferdigstilt (valgfritt)</span>
              <input value={form.completion_date} onChange={(e) => setForm({...form, completion_date: e.target.value})} placeholder="f.eks. mars 2026" className="mt-1 w-full rounded-md border border-[#D5D0C7] px-3 py-2.5 text-sm" data-testid="proj-completion" />
            </label>
            <label className="flex items-center gap-2 pb-2.5"><input type="checkbox" checked={form.published} onChange={(e) => setForm({...form, published: e.target.checked})} data-testid="proj-published" /><span className="text-sm">Publisert på nettsiden</span></label>
          </div>
        </div>
        <div className="p-4 sm:p-5 border-t border-[#E2DDD5] flex items-center justify-between gap-3 sticky bottom-0 bg-white">
          {project?.id ? <button onClick={del} className="text-[#8C2F19] font-semibold text-sm inline-flex items-center gap-1.5 hover:underline" data-testid="proj-delete"><Trash2 size={14}/> Slett</button> : <span/>}
          <button onClick={save} disabled={saving || !form.title || !form.description} className="btn-primary" data-testid="proj-save">{saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Lagre</button>
        </div>
      </div>
    </div>
  );
}
